import type {
  ReceiveGearEntryMethod,
  ReceiveGearItem,
  ReceiveGearResult,
} from '@/lib/types';

const MAX_RANGE_SIZE = 10_000;

export interface ReceiveGearIntakeResult extends ReceiveGearResult {
  id: number;
  entryMethod: ReceiveGearEntryMethod;
  savedDateOfManufacture?: string | null;
  undone?: boolean;
}

export interface ParsedSerialInput {
  items: ReceiveGearItem[];
  serials: string[];
  duplicates: string[];
  errors: Array<{
    lineNumber: number;
    line: string;
    message: string;
  }>;
}

export interface SerialRangeResult {
  serials: string[];
  error: string | null;
}

export function parseOptionalDayInterval(value: string): number | null | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function parseSerialInput(input: string): ParsedSerialInput {
  const items: ReceiveGearItem[] = [];
  const errors: ParsedSerialInput['errors'] = [];
  for (const [lineIndex, rawLine] of input.split(/\r?\n/).entries()) {
    const line = rawLine.trim();
    if (!line) continue;
    const delimitedParts = line.includes(',')
      ? line.split(',').map((part) => part.trim())
      : line.includes('\t')
        ? line.split('\t').map((part) => part.trim())
        : line.split(/\s+/);
    const trailingToken = delimitedParts[delimitedParts.length - 1] ?? '';
    const hasTrailingDateToken = /^\d{4}-\d{1,2}-\d{1,2}$/.test(trailingToken);
    if (delimitedParts.length >= 2 && hasTrailingDateToken) {
      const [year, month, day] = trailingToken.split('-').map(Number);
      const parsedDate = new Date(Date.UTC(year, month - 1, day));
      const isValidDate = (
        /^\d{4}-\d{2}-\d{2}$/.test(trailingToken)
        && parsedDate.getUTCFullYear() === year
        && parsedDate.getUTCMonth() === month - 1
        && parsedDate.getUTCDate() === day
      );
      if (!isValidDate) {
        errors.push({
          lineNumber: lineIndex + 1,
          line,
          message: 'Enter a valid manufacture date in YYYY-MM-DD format.',
        });
        continue;
      }
      const serials = delimitedParts.slice(0, -1).filter(Boolean);
      if (serials.length === 0) {
        errors.push({
          lineNumber: lineIndex + 1,
          line,
          message: 'Enter a serial before the manufacture date.',
        });
        continue;
      }
      serials.forEach((serial, index) => {
        items.push({
          serial,
          ...(index === serials.length - 1
            ? { date_of_manufacture: trailingToken }
            : {}),
        });
      });
      continue;
    }
    for (const serial of line.split(/[\s,]+/).map((part) => part.trim()).filter(Boolean)) {
      items.push({ serial });
    }
  }
  const serials = items.map((item) => item.serial);
  const seen = new Set<string>();
  const duplicateKeys = new Set<string>();
  const duplicates: string[] = [];

  for (const serial of serials) {
    const key = serial.toLocaleLowerCase();
    if (seen.has(key) && !duplicateKeys.has(key)) {
      duplicates.push(serial);
      duplicateKeys.add(key);
    }
    seen.add(key);
  }

  return { items, serials, duplicates, errors };
}

export function replaceRetriedResult(
  results: ReceiveGearIntakeResult[],
  resultId: number,
  replacement: ReceiveGearResult
): ReceiveGearIntakeResult[] {
  const hasReplacementDate = (
    replacement.date_of_manufacture !== undefined
    || replacement.proposed_date_of_manufacture !== undefined
  );
  const replacementDate = hasReplacementDate
    ? (
        replacement.date_of_manufacture
        ?? replacement.proposed_date_of_manufacture
        ?? null
      )
    : undefined;
  return results.map((result) => (
    result.id === resultId
      && !(result.status === 'created' && replacement.status !== 'created')
      ? {
          ...result,
          ...replacement,
          ...(hasReplacementDate
            ? { date_of_manufacture: replacementDate }
            : {}),
          id: result.id,
          entryMethod: result.entryMethod,
          ...(
            result.savedDateOfManufacture !== undefined
            || replacement.date_of_manufacture !== undefined
              ? {
                  savedDateOfManufacture:
                    replacement.date_of_manufacture ?? null,
                }
              : {}
          ),
          undone: false,
        }
      : result
  ));
}

export function generateSerialRange(
  firstSerial: string,
  lastSerial: string,
  maxSize = MAX_RANGE_SIZE
): SerialRangeResult {
  const first = firstSerial.trim();
  const last = lastSerial.trim();
  if (!first || !last) {
    return { serials: [], error: null };
  }

  const firstMatch = first.match(/^(.*?)(\d+)$/);
  const lastMatch = last.match(/^(.*?)(\d+)$/);
  if (!firstMatch || !lastMatch) {
    return {
      serials: [],
      error: 'Both serials must end in a numeric part, such as 24A0001.',
    };
  }

  const [, firstPrefix, firstDigits] = firstMatch;
  const [, lastPrefix, lastDigits] = lastMatch;
  if (firstPrefix !== lastPrefix || firstDigits.length !== lastDigits.length) {
    return {
      serials: [],
      error: 'The first and last serial must share the same prefix and numeric width.',
    };
  }

  const start = BigInt(firstDigits);
  const end = BigInt(lastDigits);
  if (end < start) {
    return { serials: [], error: 'The last serial must be greater than or equal to the first.' };
  }

  const count = end - start + 1n;
  if (count > BigInt(maxSize)) {
    return {
      serials: [],
      error: `This range contains more than ${maxSize.toLocaleString()} serials. Split it into smaller ranges.`,
    };
  }

  const serials: string[] = [];
  for (let value = start; value <= end; value += 1n) {
    serials.push(`${firstPrefix}${value.toString().padStart(firstDigits.length, '0')}`);
  }
  return { serials, error: null };
}

function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildReceiveGearCsv(
  batchId: string,
  batchName: string,
  results: Array<
    ReceiveGearResult & {
      savedDateOfManufacture?: string | null;
      undone?: boolean;
    }
  >
): string {
  const rows = [
    [
      'batch_id',
      'batch_name',
      'serial',
      'manufacture_date',
      'status',
      'message',
      'asset_id',
    ],
    ...results.map((result) => {
      const savedDate = result.savedDateOfManufacture !== undefined
        ? result.savedDateOfManufacture
        : result.date_of_manufacture;
      return [
        batchId,
        batchName,
        result.serial,
        result.status === 'failed' ? '' : savedDate ?? '',
        result.undone ? 'undone' : result.status,
        result.message,
        result.asset_id ?? '',
      ];
    }),
  ];
  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
}
